import { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMeals, deleteMeal } from "../api/meals";
import { AuthContext } from "../context/AuthContext";

function renderNotes(text){
  return text.split("\n").map((line, i, arr)=>{
    const parts = line.split(/(https?:\/\/[^\s]+)/g);
    return (
      <span key={i}>
        {parts.map((part, j)=>
          /^https?:\/\//.test(part)
            ? <a key={j} href={part} target="_blank" rel="noreferrer noopener" className="noteLink">{part}</a>
            : part
        )}
        {i < arr.length - 1 && <br />}
      </span>
    );
  });
}

function MealModal({ meal, onClose, onEdit, onPlanner, onDelete }){
  return(
    <div className="modalOverlay" onClick={onClose}>
      <div className="modalBox" onClick={e=>e.stopPropagation()}>

        <button className="modalClose" onClick={onClose}>×</button>

        <h2 style={{marginTop:0, marginRight:32}}>{meal.name}</h2>

        {meal.servings && (
          <p className="mealServes" style={{marginBottom:10}}>Serves {meal.servings}</p>
        )}

        {(meal.tags||[]).length > 0 && (
          <div className="mealTags" style={{marginBottom:14}}>
            {meal.tags.map(tag=>(
              <span key={tag} className="mealTag">{tag}</span>
            ))}
          </div>
        )}

        {(meal.ingredients||[]).length > 0 && (
          <>
            <h3 className="modalSectionTitle">Ingredients</h3>
            <ul className="modalIngredientList">
              {meal.ingredients.map((ing,i)=>(
                <li key={i}>
                  {typeof ing === "string"
                    ? ing
                    : [ing.quantity, ing.unit, ing.name].filter(Boolean).join(" ")}
                </li>
              ))}
            </ul>
          </>
        )}

        {meal.notes && (
          <>
            <h3 className="modalSectionTitle">Notes &amp; Links</h3>
            <div className="recipeNotes">{renderNotes(meal.notes)}</div>
          </>
        )}

        <div className="formActions" style={{marginTop:20}}>
          <button onClick={onEdit}>Edit Recipe</button>
          <button className="addToPlannerBtn" onClick={onPlanner}>+ Add to Planner</button>
          <button className="deleteBtn" onClick={onDelete}>Delete</button>
        </div>

      </div>
    </div>
  );
}

export default function MealsPage({ meals, setMeals }){

  const { householdId } = useContext(AuthContext);
  const navigate = useNavigate();

  const [search, setSearch]         = useState("");
  const [activeTags, setActiveTags] = useState([]);
  const [selectedMeal, setSelectedMeal] = useState(null);

  useEffect(()=>{
    if(!householdId) return;
    getMeals(householdId).then(setMeals);
  },[householdId, setMeals]);

  async function handleDelete(meal){
    if(!window.confirm(`Delete "${meal.name}"? This cannot be undone.`)) return;
    await deleteMeal(meal.id);
    setMeals(prev=>prev.filter(m=>m.id!==meal.id));
    setSelectedMeal(null);
  }

  const allTags = [...new Set(meals.flatMap(m=>m.tags||[]))].sort();

  function toggleTag(tag){
    setActiveTags(prev=>
      prev.includes(tag) ? prev.filter(t=>t!==tag) : [...prev, tag]
    );
  }

  const filtered = meals.filter(meal=>{
    const matchesSearch = !search ||
      meal.name.toLowerCase().includes(search.toLowerCase());
    const matchesTags = activeTags.length === 0 ||
      activeTags.every(t=>(meal.tags||[]).includes(t));
    return matchesSearch && matchesTags;
  });

  return(

    <div>

      <div className="pageHeader">
        <h2>Meal Library</h2>
        <button onClick={()=>navigate("/meals/new")}>+ Add Meal</button>
      </div>

      <div className="mealsControls">

        <input
          className="searchInput"
          placeholder="Search meals..."
          value={search}
          onChange={e=>setSearch(e.target.value)}
        />

        {allTags.length > 0 && (
          <div className="tagFilters">
            {allTags.map(tag=>(
              <button
                key={tag}
                className={`tagFilterChip${activeTags.includes(tag) ? " active" : ""}`}
                onClick={()=>toggleTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

      </div>

      {filtered.length === 0 && (
        <p className="textMuted">
          {meals.length === 0
            ? "No meals yet. Add one to get started."
            : "No meals match your search."}
        </p>
      )}

      <div className="mealGrid">
        {filtered.map(meal=>(
          <div
            key={meal.id}
            className="mealCard mealCardClickable"
            onClick={()=>setSelectedMeal(meal)}
          >

            <div className="mealCardHeader">
              <h3>{meal.name}</h3>
            </div>

            {meal.servings && (
              <p className="mealServes">Serves {meal.servings}</p>
            )}

            {(meal.tags||[]).length > 0 && (
              <div className="mealTags">
                {meal.tags.map(tag=>(
                  <span key={tag} className="mealTag">{tag}</span>
                ))}
              </div>
            )}

            <p className="mealCardHint">Click for full recipe</p>

            <div className="mealCardActions" onClick={e=>e.stopPropagation()}>
              <button
                className="editBtn"
                onClick={()=>navigate(`/meals/edit/${meal.id}`)}
              >
                Edit
              </button>
              <button
                className="addToPlannerBtn"
                onClick={()=>navigate(`/meals/plan/${meal.id}`)}
              >
                + Planner
              </button>
              <button
                className="deleteBtn"
                onClick={()=>handleDelete(meal)}
              >
                Delete
              </button>
            </div>

          </div>
        ))}
      </div>

      {selectedMeal && (
        <MealModal
          meal={selectedMeal}
          onClose={()=>setSelectedMeal(null)}
          onEdit={()=>navigate(`/meals/edit/${selectedMeal.id}`)}
          onPlanner={()=>navigate(`/meals/plan/${selectedMeal.id}`)}
          onDelete={()=>handleDelete(selectedMeal)}
        />
      )}

    </div>

  );

}
